import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {useDocumentMediaRemarks} from '../../../../../../hooks/document/useDocumentMediaRemarks';
import {IDocumentMediaRemark} from '../../../../../../api/types/document.type';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {ReactComponent as AddIcon} from '../../../../../../assets/images/add.svg';
import './DocumentMediaRemarks.scss';
import {Box, Typography} from '@mui/material';
import {RootState} from '../../../../../../redux/store';
import {useSelector} from 'react-redux';
import {DocumentMediaRemark, RemarkAction} from './DocumentMediaRemark';
import {getRandomHexColor} from '../../../../../../utils/utils';
import ArrToastNotification, {
  ArrStatusToastProps,
} from '../../../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../../../shared/constants';
import ReactDOM from 'react-dom';
import {ArrAVPreviewContext} from '../../../../../components/avdoc-preview/arr-av-provider/ArrAVPreviewProvider';
import {orderBy} from 'lodash';

interface DocumentMediaRemarksProps {
  loginUsername: string;
  annotationLayerId?: string;
  showRemarks: boolean;
}

const DocumentMediaRemarks: React.FC<DocumentMediaRemarksProps> = ({
  loginUsername,
  annotationLayerId,
  showRemarks,
}: DocumentMediaRemarksProps) => {
  const [addRemark, setAddRemark] = useState<boolean>();
  const [remarksLists, setRemarksLists] = useState<IDocumentMediaRemark[]>([]);
  const [savedRemark, setSavedRemark] = useState<boolean>(false);
  const messageRef = useRef<string>('');

  const avPreviewcontext = useContext(ArrAVPreviewContext);
  const {isPlayerReady, addMarkers} = avPreviewcontext;

  const activeDocument = useSelector(
    (state: RootState) => state.docView.activeDoc
  );

  const {data: remarksListsResponse, refetch: RefetchMediaRemarks} =
    useDocumentMediaRemarks(
      {
        audioDocumentId: Number.parseInt(activeDocument?.documentId ?? '0'),
      },
      !!activeDocument?.documentId
    );

  useEffect(() => {
    const getRemarksList = orderBy(
      (remarksListsResponse ?? []).map((item: IDocumentMediaRemark) => {
        item.remarkColor = getRandomHexColor();
        return item;
      }),
      [(item) => Date.parse(item.createdDate)]
    );
    setRemarksLists(getRemarksList);
  }, [addMarkers, remarksListsResponse]);

  useEffect(() => {
    isPlayerReady && addMarkers(remarksLists);
  }, [addMarkers, isPlayerReady, remarksLists]);

  const onAddRemark = useCallback(() => {
    setAddRemark(true);
  }, []);

  const onAddCancelRemark = useCallback(() => {
    setAddRemark(false);
  }, []);

  const refreshData = useCallback(() => {
    RefetchMediaRemarks();
    setSavedRemark(true);
  }, [RefetchMediaRemarks]);

  const onAddedRemark = useCallback(() => {
    messageRef.current = t('docRemarks.addedRemark');
    setAddRemark(false);
    refreshData();
  }, [refreshData]);

  const onSavedRemark = useCallback(
    (action?: RemarkAction) => {
      messageRef.current = t(
        action === 'delete'
          ? 'docRemarks.deletedRemark'
          : 'docRemarks.updatedRemark'
      );
      refreshData();
    },
    [refreshData]
  );

  const onClosedToast = useCallback(() => {
    messageRef.current = '';
    setSavedRemark(false);
  }, []);

  const SuccessToast = useCallback((props: ArrStatusToastProps) => {
    return ReactDOM.createPortal(
      <ArrToastNotification {...props} />,
      document.body
    );
  }, []);

  if (!showRemarks) {
    return <Fragment />;
  }

  return (
    <div data-testid={'doc-remarks-list'} className="doc-remarks-master">
      <div
        className="doc-remarks-list"
        data-testid={`document-media-remarks-reviewer-remarks-remark`}>
        <Box className={'reamrks-header'}>
          <Typography
            className="typo-text subhead5_size title"
            data-testid={`document-media-remarks-reviewer-remarks`}>
            {t('docRemarks.reviewerRemarks')}
          </Typography>
        </Box>
        <Box className={'remarks-container'}>
          <Box className={'sub-header'}>
            <ArrButton
              size="small"
              variant={'secondary'}
              dataTestId="action-button"
              startIcon={<AddIcon className="add-remark-icon" />}
              aria-label={t('docRemarks.remark')}
              onClick={onAddRemark}
            >
              {t('docRemarks.remark')}
            </ArrButton>
          </Box>

          {addRemark && (
            <DocumentMediaRemark
              key={'add-remark'}
              userName={loginUsername}
              annotationLayerId={annotationLayerId}
              documentGUID={activeDocument?.documentGUID}
              ingestionGUID={activeDocument?.ingestionGUID}
              documentId={activeDocument?.documentId}
              miniMediaLength={activeDocument?.miniMediaLength}
              onSavedRemark={onAddedRemark}
              onCancelRemark={onAddCancelRemark}
            />
          )}
          {remarksLists.map((remark) => {
            return (
              <DocumentMediaRemark
                key={remark.documentAudioRemarkID}
                userName={loginUsername}
                annotationLayerId={annotationLayerId}
                documentGUID={activeDocument?.documentGUID}
                ingestionGUID={activeDocument?.ingestionGUID}
                documentId={activeDocument?.documentId}
                miniMediaLength={activeDocument?.miniMediaLength}
                viewRemark={remark}
                onSavedRemark={onSavedRemark}
              />
            );
          })}
        </Box>
      </div>
      {savedRemark && (
        <SuccessToast
          message={messageRef.current}
          onClose={onClosedToast}
          status={[ToastType.Success]}
        />
      )}
    </div>
  );
};

export {DocumentMediaRemarks};
